import { useParams, Link } from "react-router-dom";
import ProductCard from "@/components/products/ProductCard";
import { Star, MapPin, Clock, Truck, Search, X, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useState, useMemo } from "react";
import { useStorefrontStoreById, useStoreProducts } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";

const StoreDetail = () => {
  const { id } = useParams<{ id: string }>();
  const storeId = Number(id);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: store, isLoading: isLoadingStore } = useStorefrontStoreById(storeId, Number.isFinite(storeId));
  const { data: productsData, isLoading: isLoadingProducts } = useStoreProducts(storeId, 1, 100, Number.isFinite(storeId));

  const storeProducts = useMemo(() => {
    let result = productsData?.items ?? [];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
    }

    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }

    return result;
  }, [productsData, searchQuery, selectedCategory]);

  const storeCategories = useMemo(() => {
    const items = productsData?.items ?? [];
    return [...new Set(items.map((p) => p.category))];
  }, [productsData]);

  if (isLoadingStore) {
    return (
      <div className="min-h-screen bg-background">
        <Skeleton className="h-56 w-full" />
        <div className="container mx-auto px-4 py-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="font-heading text-xl font-bold mb-2">Store not found</p>
          <Link to="/stores"><Button variant="outline">Back to Stores</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="relative h-48 md:h-56 overflow-hidden">
        <img src={store.image} alt={store.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>

      <div className="container mx-auto px-4 -mt-16 relative z-10 pb-12">
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/stores" className="hover:text-foreground transition-colors">Stores</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground">{store.name}</span>
        </nav>

        <div className="bg-card rounded-2xl border border-border/50 p-5 md:p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <img src={store.image} alt={store.name} className="w-20 h-20 rounded-xl object-cover border border-border/50" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className="font-heading text-2xl font-bold">{store.name}</h1>
                <Badge variant={store.isOpen ? "default" : "secondary"}>{store.isOpen ? "Open" : "Closed"}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{store.description || store.address}</p>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /> {store.rating} ({store.reviewCount})</span>
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {store.distance}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {store.deliveryTime}</span>
                <span className="flex items-center gap-1"><Truck className="w-4 h-4" /> ₹{store.deliveryFee} delivery</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search in this store..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-card border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {storeCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !selectedCategory ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground"
              }`}
            >
              All
            </button>
            {storeCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {isLoadingProducts ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-2xl" />
            ))}
          </div>
        ) : storeProducts.length > 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {storeProducts.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </motion.div>
        ) : (
          <p className="text-center text-muted-foreground py-12">No products available in this store yet.</p>
        )}
      </div>
    </div>
  );
};

export default StoreDetail;
